/**
 * NewEnrollmentModal
 *
 * Modal to begin a new enrollment process: select provider, payer, enrollment type.
 * Optional billing entity when type is "billing-through". Mantine Modal, Select, Button.
 * PHI-safe: provider/payer names only.
 */

import { Button, Group, Modal, Select, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect } from 'react';
import type {
  BillingEntityOption,
  Enrollment,
  EnrollmentType,
  PayerOption,
  ProviderOption,
} from '../types';

const enrollmentTypeOptions: { value: EnrollmentType; label: string }[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'group', label: 'Group' },
  { value: 'billing-through', label: 'Billing through' },
];

interface NewEnrollmentModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated: (enrollment: Enrollment) => void;
  providers: ProviderOption[];
  payers: PayerOption[];
  billingEntities: BillingEntityOption[];
}

export function NewEnrollmentModal({
  opened,
  onClose,
  onCreated,
  providers,
  payers,
  billingEntities,
}: NewEnrollmentModalProps): JSX.Element {
  const form = useForm({
    initialValues: {
      providerId: '',
      payerId: '',
      enrollmentType: 'individual' as EnrollmentType,
      billingEntityId: '',
    },
    validate: {
      providerId: (v) => (v ? null : 'Select a provider'),
      payerId: (v) => (v ? null : 'Select a payer'),
      enrollmentType: (v) => (v ? null : 'Select enrollment type'),
      billingEntityId: (v, values) =>
        values.enrollmentType === 'billing-through' && !v ? 'Select billing entity' : null,
    },
  });

  useEffect(() => {
    if (opened) form.reset();
  }, [opened]);

  const handleSubmit = form.onSubmit((values) => {
    const provider = providers.find((p) => p.id === values.providerId);
    const payer = payers.find((p) => p.id === values.payerId);
    const billingEntity =
      values.enrollmentType === 'billing-through'
        ? billingEntities.find((b) => b.id === values.billingEntityId)
        : undefined;

    if (!provider || !payer) return;

    const now = new Date().toISOString();
    const newEnrollment: Enrollment = {
      id: `e${Date.now()}`,
      providerId: provider.id,
      providerName: provider.name,
      payerId: payer.id,
      payerName: payer.name,
      enrollmentType: values.enrollmentType,
      status: 'not-started',
      billingReady: false,
      nextAction: 'Collect required documents and initiate application',
      updatedAt: now,
      ...(values.enrollmentType === 'billing-through' &&
        billingEntity && {
          billingThroughEntityId: billingEntity.id,
          billingThroughEntityName: billingEntity.name,
        }),
    };

    onCreated(newEnrollment);
    showNotification({
      color: 'green',
      message: `Enrollment started for ${provider.name} with ${payer.name}`,
    });
    form.reset();
    onClose();
  });

  const handleClose = (): void => {
    form.reset();
    onClose();
  };

  const isBillingThrough = form.values.enrollmentType === 'billing-through';

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Start new enrollment"
      size="sm"
      centered
      closeOnClickOutside={!form.isDirty()}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Select
            label="Provider"
            placeholder="Select provider"
            data={providers.map((p) => ({ value: p.id, label: p.name }))}
            searchable
            required
            {...form.getInputProps('providerId')}
          />
          <Select
            label="Payer"
            placeholder="Select payer"
            data={payers.map((p) => ({ value: p.id, label: p.name }))}
            searchable
            required
            {...form.getInputProps('payerId')}
          />
          <Select
            label="Enrollment type"
            data={enrollmentTypeOptions}
            {...form.getInputProps('enrollmentType')}
          />
          {isBillingThrough && (
            <Select
              label="Billing entity"
              description="Entity the provider will bill under"
              placeholder="Select billing entity"
              data={billingEntities.map((b) => ({ value: b.id, label: b.name }))}
              required={isBillingThrough}
              {...form.getInputProps('billingEntityId')}
            />
          )}
          <Group justify="flex-end" mt="md">
            <Button variant="default" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">Start enrollment</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
